import { el, firstNonEmpty, joinFactors, normalizeMark, stringify } from "../dom.js"
import { t, titleForKey } from "../i18n.js"
import { renderMarkdownHtml } from "../markdown.js"

const VECTOR_ORDER = ["S", "P", "Sch", "C"]
const DEFAULT_FACTORS = ["h", "s", "e", "hy", "k", "p", "d", "m"]

function markSpan(mark) {
  if (mark === "+") return el("span", { className: "szondi-plan__mark szondi-plan__mark--plus", "aria-hidden": "true" }, "+")
  if (mark === "−") return el("span", { className: "szondi-plan__mark szondi-plan__mark--minus", "aria-hidden": "true" }, "−")
  return null
}

// Historical Szondi reports still embed raw-score / reaction tables; the protocol gallery
// replaced them, so those keys are skipped rather than shown collapsed.
const HIDDEN_TABLE_KEYS = new Set(["raw_scores", "reactions"])
const PROTOCOL_SLOT_COUNT = 8

function wrapCollapsed(summaryText, node, className = "report-details") {
  const details = el("details", { className })
  details.append(el("summary", {}, summaryText))
  details.append(node)
  return details
}

export function renderTableBlock(block, { locale }) {
  if (HIDDEN_TABLE_KEYS.has(block.key)) return null
  const columns = Array.isArray(block.columns) ? block.columns : []
  const rows = Array.isArray(block.rows) ? block.rows : []
  const wrap = el("div", { className: "table-scroll" })
  if (block.title_key) wrap.append(el("h3", { className: "phase-title" }, t(block.title_key, { locale })))
  if (block.subtitle_key) wrap.append(el("h4", { className: "interpretation-blocks__heading" }, t(block.subtitle_key, { locale })))
  if (block.disclaimer_key) wrap.append(el("p", { className: "hint" }, t(block.disclaimer_key, { locale })))

  const keys = []
  const headers = []
  const seen = new Set()
  for (const col of columns) {
    const key = col?.key
    if (!key || seen.has(key)) continue
    seen.add(key)
    keys.push(key)
    if (key === "row") {
      headers.push(firstNonEmpty(t(col.header_key, { locale }), t("szondi.result.factor", { locale }), col.header, "Фактор"))
    } else {
      headers.push(firstNonEmpty(t(col.header_key, { locale }), col.header, key))
    }
  }

  const table = el("table", { className: "report-table report-table--bordered report-table--compact" })
  const thead = el("thead")
  const hr = el("tr")
  for (const h of headers) hr.append(el("th", {}, h))
  thead.append(hr)
  table.append(thead)

  const tbody = el("tbody")
  for (const row of rows) {
    const tr = el("tr")
    const cells = row?.cells || {}
    for (const key of keys) {
      if (key === "row" || key === "scale") {
        const label = firstNonEmpty(
          t(row.row_label_key, { locale }),
          row.row_key,
          row.slot,
          cells.row,
          cells.scale,
          ""
        )
        tr.append(el("td", {}, label || ""))
        continue
      }
      if (key === "content" || key === "answer_text" || cells[`${key}_prose`]) {
        tr.append(el("td", {}, stringify(cells[key] ?? row[key] ?? row.content_key ?? row.answer_text)))
        continue
      }
      let value = cells[key]
      if (value == null && row[key] != null) value = row[key]
      if (value == null && key.endsWith("_key") === false && row[`${key}_key`]) {
        value = t(row[`${key}_key`], { locale })
      }
      tr.append(el("td", {}, stringify(value)))
    }
    tbody.append(tr)
  }
  table.append(tbody)
  wrap.append(table)
  return wrap
}

export function renderKeyedMetricsBlock(block, { locale }) {
  const root = el("div", { className: "keyed-metrics" })
  if (block.title_key) root.append(el("h3", { className: "phase-title" }, t(block.title_key, { locale })))
  if (block.disclaimer_key) root.append(el("p", { className: "hint" }, t(block.disclaimer_key, { locale })))
  const list = el("ul", { className: "report-list" })
  for (const metric of block.metrics || []) {
    const label = t(metric.label_key, { locale, defaultValue: metric.label_key })
    list.append(el("li", {}, `${label}: `, el("strong", {}, stringify(metric.value))))
  }
  root.append(list)
  return root
}

export function renderRegistrationSheetBlock(block, { locale }) {
  const vectorGroups = block.vector_groups || {}
  const factors = []
  const vectorHeaders = []
  for (const code of VECTOR_ORDER) {
    const fs = vectorGroups[code]
    if (!fs?.length) continue
    vectorHeaders.push(firstNonEmpty(t(`szondi.result.vector_header.${code}`, { locale }), code))
    factors.push(...fs)
  }
  if (!factors.length) factors.push(...DEFAULT_FACTORS)

  const section = el("section", { className: "szondi-registration-sheet" })
  const title = t(block.plane_heading_key, { locale })
  if (title) section.append(el("h3", { className: "phase-title" }, title))

  const table = el("table", {
    className: "report-table report-table--bordered report-table--compact szondi-registration-sheet__table",
  })
  const thead = el("thead")
  const row1 = el("tr")
  row1.append(el("th", { rowspan: "2" }, t("szondi.result.registration_series_column", { locale })))
  for (const vh of vectorHeaders) row1.append(el("th", { colspan: "2" }, vh))
  thead.append(row1)
  const row2 = el("tr")
  for (const f of factors) row2.append(el("th", {}, f))
  thead.append(row2)
  table.append(thead)

  const tbody = el("tbody")
  for (const row of block.sheet?.rows || []) {
    const tr = el("tr")
    tr.append(el("th", { scope: "row" }, row.series_label || ""))
    const cells = row.cells || {}
    for (const f of factors) {
      const mark = normalizeMark(cells[f])
      const td = el("td", {
        className: mark ? "szondi-mark-cell szondi-mark-cell--filled" : "szondi-mark-cell",
      })
      const span = markSpan(mark)
      if (span) td.append(span)
      tr.append(td)
    }
    tbody.append(tr)
  }
  table.append(tbody)
  section.append(el("div", { className: "table-scroll" }, table))
  return section
}

export function renderProfileGridBlock(block, { locale }) {
  // ППП and ЭКП must not be summed; skip legacy combined grids.
  if (
    block.key === "profile_grid_combined" ||
    block.plane_heading_key === "szondi.result.marks_combined" ||
    String(block.grid_id || "").includes("combined")
  ) {
    return null
  }
  const grid = block.grid || {}
  const factors = grid.factors?.length ? grid.factors : DEFAULT_FACTORS
  let vectorHeaders = []
  if (grid.vectors && typeof grid.vectors === "object") {
    for (const code of VECTOR_ORDER) {
      if (grid.vectors[code]) vectorHeaders.push(code)
    }
    if (!vectorHeaders.length) vectorHeaders = Object.keys(grid.vectors)
  } else {
    vectorHeaders = VECTOR_ORDER.slice()
  }

  const cellIndex = new Map()
  for (const cell of grid.cells || []) {
    cellIndex.set(`${cell.factor}|${stringify(cell.row)}`, normalizeMark(cell.mark))
  }

  const section = el("section", { className: "szondi-plans" })
  const plan = el("div", { className: "szondi-plan" })
  const title = t(block.plane_heading_key, { locale })
  if (title) plan.append(el("h3", { className: "phase-title szondi-plan__title" }, title))

  const gridEl = el("div", {
    className: "szondi-plan__grid",
    role: "grid",
    "aria-label": firstNonEmpty(title, t("szondi.result.profile_grid_caption", { locale })),
  })
  gridEl.append(el("div", { className: "szondi-plan__corner" }))
  for (const v of vectorHeaders) gridEl.append(el("div", { className: "szondi-plan__vector" }, v))
  gridEl.append(el("div", { className: "szondi-plan__corner szondi-plan__corner--factor" }))
  for (const f of factors) gridEl.append(el("div", { className: "szondi-plan__factor" }, f))

  for (const rowId of grid.positive_rows || []) {
    const label = stringify(rowId)
    gridEl.append(el("div", { className: "szondi-plan__row-label szondi-plan__row-label--positive" }, label))
    for (const f of factors) {
      const mark = cellIndex.get(`${f}|${label}`) || ""
      const cell = el("div", {
        className: mark
          ? "szondi-plan__cell szondi-plan__cell--positive szondi-plan__cell--filled"
          : "szondi-plan__cell szondi-plan__cell--positive",
      })
      if (mark === "+") cell.append(markSpan(mark))
      gridEl.append(cell)
    }
  }

  ;(grid.negative_rows || []).forEach((rowId, index) => {
    const label = stringify(rowId)
    const baseline = index === 0 && (grid.positive_rows || []).length > 0
    gridEl.append(
      el(
        "div",
        {
          className: baseline
            ? "szondi-plan__row-label szondi-plan__row-label--negative szondi-plan__row-label--baseline"
            : "szondi-plan__row-label szondi-plan__row-label--negative",
        },
        label
      )
    )
    for (const f of factors) {
      const mark = cellIndex.get(`${f}|${label}`) || ""
      const cell = el("div", {
        className: [
          "szondi-plan__cell",
          "szondi-plan__cell--negative",
          mark ? "szondi-plan__cell--filled" : "",
          baseline ? "szondi-plan__cell--baseline" : "",
        ]
          .filter(Boolean)
          .join(" "),
      })
      if (mark === "−") cell.append(markSpan(mark))
      gridEl.append(cell)
    }
  })

  gridEl.append(
    el("div", { className: "szondi-plan__row-label szondi-plan__row-label--reaction" }, t("szondi.result.reaction", { locale }))
  )
  const reactionLabels = grid.reaction_labels || {}
  const reactionKinds = { ...(grid.reaction_kinds_by_factor || {}), ...(grid.reaction_kinds || {}) }
  for (const f of factors) {
    const kind = reactionKinds[f]
    const tip = kind ? t(`szondi.reactions.${kind}`, { locale }) : ""
    gridEl.append(
      el("div", { className: "szondi-plan__reaction", title: tip || undefined }, reactionLabels[f] || "")
    )
  }

  plan.append(el("div", { className: "szondi-plan__scroll" }, gridEl))
  section.append(plan)
  return section
}

export function renderVectorSummariesBlock(block, { locale }) {
  const tables = (block.tables || []).filter((table) => table.key !== "combined")
  if (!tables.length) return null

  const root = el("div", { className: "szondi-vectors" })
  if (block.title_key) root.append(el("h3", { className: "phase-title" }, t(block.title_key, { locale })))

  for (const table of tables) {
    if (table.subtitle_key) {
      root.append(el("h4", { className: "interpretation-blocks__heading" }, t(table.subtitle_key, { locale })))
    }
    const tbl = el("table", { className: "report-table" })
    const thead = el("thead")
    thead.append(
      el(
        "tr",
        {},
        el("th", {}, t("szondi.result.vector_column", { locale })),
        el("th", {}, t("szondi.result.positive_column", { locale })),
        el("th", {}, t("szondi.result.negative_column", { locale })),
        el("th", {}, t("szondi.result.total", { locale })),
        el("th", {}, t("szondi.result.loaded_column", { locale }))
      )
    )
    tbl.append(thead)
    const tbody = el("tbody")
    for (const row of table.rows || []) {
      const loaded = !!row.loaded
      const loadedLabel = loaded
        ? t("szondi.result.vector_loaded", { locale })
        : t("szondi.result.vector_not_loaded", { locale })
      const label = firstNonEmpty(t(row.label_key, { locale }), row.code)
      const factors = joinFactors(row.factors)
      tbody.append(
        el(
          "tr",
          {},
          el(
            "td",
            {},
            el("strong", {}, label),
            factors ? el("span", { className: "muted" }, ` (${factors})`) : null
          ),
          el("td", {}, stringify(row.positive)),
          el("td", {}, stringify(row.negative)),
          el("td", {}, stringify(row.total)),
          el(
            "td",
            {},
            el("span", { className: loaded ? "tag tag--ok" : "tag tag--muted" }, loadedLabel)
          )
        )
      )
    }
    tbl.append(tbody)
    root.append(el("div", { className: "table-scroll" }, tbl))
  }
  return root
}

export function renderMarkdownBlock(block, { locale }) {
  const root = el("div", { className: "md-block content" })
  if (block.title_key) root.append(el("h3", { className: "phase-title" }, t(block.title_key, { locale })))
  const body = firstNonEmpty(t(block.body_key, { locale }), block.body)
  const md = renderMarkdownHtml(body, { className: "md-block__body" })
  if (md) root.append(md)

  if (block.key === "profile_contrast" && Array.isArray(block.conflict_factors) && block.conflict_factors.length) {
    root.append(
      el(
        "p",
        { className: "hint" },
        `${t("szondi.result.profile_contrast_title", { locale })} — ${joinFactors(block.conflict_factors)}`
      )
    )
  }

  if (block.key === "syndromes") {
    const ul = el("ul", { className: "report-list" })
    for (const item of block.items || []) {
      const text = t(item.interpretation_key || `szondi.syndromes.${item.key}`, { locale })
      if (text) ul.append(el("li", {}, text))
    }
    if (ul.childNodes.length) root.append(ul)
  } else if (block.key === "typology") {
    for (const item of block.items || []) {
      const text = t(item.interpretation_key || `szondi.typology.${item.key}`, { locale })
      if (!text) continue
      const box = el("div", { className: "szondi-typology" }, el("p", {}, text))
      if (item.match_score != null && item.match_score !== "") {
        box.append(
          el(
            "p",
            { className: "hint" },
            t("unified_report.typology.match_score", { locale, score: stringify(item.match_score) })
          )
        )
      }
      root.append(box)
    }
  } else if (block.variant === "interpretations") {
    for (const item of block.items || []) {
      const text = t(item.body_key, { locale })
      if (!text) continue
      const card = el("section", { className: "szondi-typology" })
      if (item.title_key) card.append(el("h4", { className: "phase-title" }, t(item.title_key, { locale })))
      card.append(el("p", {}, text))
      root.append(card)
    }
  }

  return root
}

export function renderNotificationBlock(block, { locale }) {
  const level = block.level || "info"
  let message = firstNonEmpty(block.body, block.message, block.text)
  if (!message && block.body_key) {
    const params = { locale, ...(block.body_params || {}) }
    if (params.verdict_key) params.verdict = titleForKey(params.verdict_key, locale)
    message = t(block.body_key, params)
  }
  return el("p", { className: `notification notification--${level}` }, message || "")
}

function portraitSrc(assetDir, series, slot, opts = {}, explicitSrc) {
  if (explicitSrc) return explicitSrc
  const file = `${assetDir}/p${series}${slot}.webp`
  const mapped = opts.portraitUrls && opts.portraitUrls[file]
  if (mapped) return mapped
  const base = String(opts.portraitBase || "/static/portraits").replace(/\/$/, "")
  return `${base}/${file}`
}

function renderPortraitCard({ series, slot, assetDir, opts, mark, factor, disabled, src }) {
  const card = el("figure", {
    className: [
      "szondi-protocol__card",
      mark === "+" ? "szondi-protocol__card--plus" : "",
      mark === "−" ? "szondi-protocol__card--minus" : "",
      disabled ? "szondi-protocol__card--disabled" : "",
    ]
      .filter(Boolean)
      .join(" "),
  })
  card.append(
    el("img", {
      className: "szondi-protocol__img",
      src: portraitSrc(assetDir, series, slot, opts, src),
      alt: factor ? `${factor}` : `p${series}${slot}`,
      loading: "lazy",
      width: assetDir === "szondi" ? "264" : "320",
      height: assetDir === "szondi" ? "312" : "480",
    })
  )
  if (mark) {
    card.append(
      el(
        "span",
        {
          className:
            mark === "+"
              ? "szondi-protocol__badge szondi-protocol__badge--plus"
              : "szondi-protocol__badge szondi-protocol__badge--minus",
        },
        mark
      )
    )
  }
  if (factor) card.append(el("figcaption", { className: "szondi-protocol__caption" }, factor))
  return card
}

function renderSelectedPicks(positives, negatives, assetDir, series, opts) {
  const row = el("div", { className: "szondi-protocol__picks" })
  for (const [picks, mark] of [
    [positives, "+"],
    [negatives, "−"],
  ]) {
    for (const pick of picks) {
      row.append(
        renderPortraitCard({
          series,
          slot: Number(pick.slot),
          assetDir,
          opts,
          mark,
          factor: pick.factor || "",
          src: pick.src,
        })
      )
    }
  }
  return row
}

function renderProtocolStep(step, { locale, assetDir, opts, frontChosen }) {
  const series = Number(step.series) || 0
  const plane = step.plane === "rear" ? "rear" : "front"
  const planeKey = plane === "rear" ? "szondi.result.marks_rear" : "szondi.result.marks_front"
  const article = el("article", { className: `szondi-protocol__step szondi-protocol__step--${plane}` })
  article.append(
    el(
      "h4",
      { className: "szondi-protocol__step-title" },
      `${t("szondi.result.protocol_series", { locale, n: series })} · ${t(planeKey, { locale })}`
    )
  )

  const positives = Array.isArray(step.positives) ? step.positives : []
  const negatives = Array.isArray(step.negatives) ? step.negatives : []
  const chosen = new Map()
  for (const pick of positives) chosen.set(Number(pick.slot), { pick, mark: "+" })
  for (const pick of negatives) chosen.set(Number(pick.slot), { pick, mark: "−" })

  const selectedView = el("div", { className: "szondi-protocol__selected" })
  selectedView.append(renderSelectedPicks(positives, negatives, assetDir, series, opts))
  article.append(selectedView)

  const seriesView = el("div", { className: "szondi-protocol__series" })
  const grid = el("div", { className: `szondi-protocol__grid szondi-protocol__grid--${assetDir}` })
  const disabled = plane === "rear" ? frontChosen.get(series) || new Set() : new Set()
  for (let slot = 1; slot <= PROTOCOL_SLOT_COUNT; slot++) {
    const info = chosen.get(slot)
    grid.append(
      renderPortraitCard({
        series,
        slot,
        assetDir,
        opts,
        mark: info?.mark || "",
        factor: info?.pick?.factor || "",
        disabled: disabled.has(slot),
      })
    )
  }
  seriesView.append(grid)
  article.append(seriesView)
  return article
}

export function renderProtocolStepsBlock(block, opts = {}) {
  const { locale } = opts
  const steps = Array.isArray(block.steps) ? block.steps : []
  if (!steps.length) return null
  const assetDir = block.asset_dir || "szondi_orig"
  const frontChosen = new Map()
  for (const step of steps) {
    if (step.plane !== "front") continue
    const used = new Set()
    for (const pick of [...(step.positives || []), ...(step.negatives || [])]) {
      const slot = Number(pick.slot)
      if (slot) used.add(slot)
    }
    frontChosen.set(Number(step.series), used)
  }

  const root = el("section", {
    className: `szondi-protocol szondi-protocol--selected szondi-protocol--${assetDir}`,
  })
  const header = el("div", { className: "szondi-protocol__header" })

  const toggle = el("div", { className: "szondi-protocol__toggle", role: "group" })
  const selectedBtn = el(
    "button",
    { type: "button", className: "szondi-protocol__toggle-btn is-active", "aria-pressed": "true" },
    t("szondi.result.protocol_view_selected", { locale })
  )
  const seriesBtn = el(
    "button",
    { type: "button", className: "szondi-protocol__toggle-btn", "aria-pressed": "false" },
    t("szondi.result.protocol_view_series", { locale })
  )
  const setView = (view) => {
    const selected = view === "selected"
    root.classList.toggle("szondi-protocol--selected", selected)
    root.classList.toggle("szondi-protocol--series", !selected)
    selectedBtn.classList.toggle("is-active", selected)
    seriesBtn.classList.toggle("is-active", !selected)
    selectedBtn.setAttribute("aria-pressed", selected ? "true" : "false")
    seriesBtn.setAttribute("aria-pressed", selected ? "false" : "true")
  }
  selectedBtn.addEventListener("click", () => setView("selected"))
  seriesBtn.addEventListener("click", () => setView("series"))
  toggle.append(selectedBtn, seriesBtn)
  header.append(toggle)
  root.append(header)

  for (const step of steps) {
    root.append(renderProtocolStep(step, { locale, assetDir, opts, frontChosen }))
  }
  const title = t(block.title_key, { locale }) || t("szondi.result.protocol_title", { locale })
  return wrapCollapsed(title, root, "report-details szondi-protocol-details")
}

const BLOCK_RENDERERS = {
  table: renderTableBlock,
  keyed_metrics: renderKeyedMetricsBlock,
  registration_sheet: renderRegistrationSheetBlock,
  profile_grid: renderProfileGridBlock,
  vector_summaries: renderVectorSummariesBlock,
  protocol_steps: renderProtocolStepsBlock,
  markdown: renderMarkdownBlock,
  notification: renderNotificationBlock,
}

export function renderCompositeBlock(block, opts) {
  if (block?.visibility === false) return null
  const renderer = BLOCK_RENDERERS[block?.type]
  if (!renderer) return null
  try {
    return renderer(block, opts)
  } catch {
    return null
  }
}

export function renderCompositeBlocks(section, opts) {
  const data = section.data || {}
  const root = el("div", {
    className: data.presentation_class || "szondi-result composite-blocks-result content",
  })
  const blocks = Array.isArray(data.blocks) ? data.blocks : []
  let rendered = 0
  for (const block of blocks) {
    const node = renderCompositeBlock(block, opts)
    if (node) {
      root.append(node)
      rendered += 1
    }
  }
  if (!rendered) return el("pre", { className: "raw-json" }, JSON.stringify(data, null, 2))
  return root
}
